import { safetyMargins, FULL_HD, HD, LAYOUT_LEFT_PADDING as LEFT_PADDING } from '@bedrock-rdk/constants';
import { makeThemeColor, TERTIARY } from '@tornado-rdk/theme';
import { makeDefaultStylingContext } from '../tornado/utils/style';
import { SCREEN_WIDTH, SCREEN_HEIGHT } from '../tornado/utils/sizes';
import { WIDTH as SIDEBAR_WIDTH } from '../sidenav/sidenav.constants';

export const defaultContext = makeDefaultStylingContext();

export { LAYOUT_LEFT_PADDING as LEFT_PADDING } from '@bedrock-rdk/constants';

export const BOTTOM_PADDING = {
  [HD]: safetyMargins.vertical.HD,
  [FULL_HD]: safetyMargins.vertical.FULL_HD,
};

export const getContentPadding = (hasSideNav = true, screenSize = undefined) => {
  const screenDimension = screenSize || defaultContext.screenSize;

  return hasSideNav
    ? SIDEBAR_WIDTH[screenDimension] + LEFT_PADDING[screenDimension]
    : safetyMargins.horizontal[screenDimension];
};

/**
 * Layout always fits all the screen square
 */
export const WIDTH = {
  ...SCREEN_WIDTH,
};

export const HEIGHT = {
  ...SCREEN_HEIGHT,
};

export const BACKGROUND_COLOR = makeThemeColor(TERTIARY);

/**
 * The margin Lightning applies when nothing sets one. Kept for the horizontal directions, so widening the vertical
 * reach does not narrow the horizontal one — the items a row holds just past the left and right edges would
 * otherwise stop displaying their textures.
 */
const DEFAULT_BOUNDS_MARGIN = 100;

/** Share of the screen height the blocks keep placing beyond, above and below */
export const VERTICAL_LOOKAHEAD_RATIO = 0.5;

const LOOKAHEAD_QUERY_PARAMETER = 'lookahead';

/**
 * How far past the top and the bottom of the screen the blocks keep placing.
 *
 * `?lookahead=<pixels>` overrides it, so the reach can be compared on a device without a rebuild. `?lookahead=0`
 * turns it off entirely, which is the baseline to measure the rest against.
 *
 * @param {string} screenSize one of the screen size constants
 * @returns {Number} the lookahead in pixels
 */
export const getVerticalLookahead = (screenSize = defaultContext.screenSize) => {
  try {
    const parameter = new URL(document.location).searchParams.get(LOOKAHEAD_QUERY_PARAMETER);

    // NOTE: the emptiness check is what keeps a missing parameter from reading as the zero `Number` coerces it to
    if (parameter !== null && parameter !== '') {
      const override = Number(parameter);

      if (Number.isFinite(override) && override >= 0) {
        return override <= 1 ? Math.round(HEIGHT[screenSize] * override) : Math.round(override);
      }
    }
  } catch (error) {
    // No addressable location to read, so the configured share of the screen stands
  }

  return Math.round(HEIGHT[screenSize] * VERTICAL_LOOKAHEAD_RATIO);
};

/**
 * Bounds margin for the block list, which decides how far outside the screen a block goes on counting as active
 * and so goes on placing its items.
 *
 * Lightning ties the active state of a component to its bounds — `_isActive()` is `_isEnabled() &&
 * withinBoundsMargin` — so left to itself a row only starts filling as it scrolls into view. The margin is
 * inherited by everything below Blocks, and it also spares a block from the clipbox cull that would otherwise
 * force its whole subtree out of bounds, which is what lets the rows inside it activate as well.
 *
 * Both vertical entries carry the same number on purpose: the four are documented left-top-right-bottom, but the
 * bounds test reads them transposed in pairs, so a symmetric pair is right either way round.
 *
 * @param {string} screenSize one of the screen size constants
 * @returns {Array} a Lightning boundsMargin
 */
export const getBlocksBoundsMargin = (screenSize = defaultContext.screenSize) => {
  const vertical = getVerticalLookahead(screenSize);

  return [DEFAULT_BOUNDS_MARGIN, vertical, DEFAULT_BOUNDS_MARGIN, DEFAULT_BOUNDS_MARGIN];
};

export const getInitialStyle = (context = defaultContext) => {
  const { screenSize } = context;

  return {
    w: WIDTH[screenSize],
    Background: {
      w: WIDTH[screenSize],
      h: HEIGHT[screenSize],
      alpha: 0,
    },
    ContextTitle: {
      alpha: 0,
    },
    Blocks: {
      w: WIDTH[screenSize],
      boundsMargin: getBlocksBoundsMargin(screenSize),
    },
    Notifications: {},
  };
};
